import type { EchartsTooltipActionPayload } from './echarts-tooltip-action.helper';

const tooltipWrapStyle =
  'white-space:normal;overflow-wrap:anywhere;word-break:break-word;';

export const ECHARTS_TOOLTIP_DEFAULT_TITLE_STYLE =
  'font-weight:bold;font-size:13px;color:#0F204B;margin:0 0 4px;';
export const ECHARTS_TOOLTIP_LARGE_TITLE_STYLE =
  'font-weight:bold;font-size:16px;color:#0F204B;margin:0 0 6px;';
export const ECHARTS_TOOLTIP_HINT_STYLE =
  'font-size:12px;line-height:1.4;color:#000;margin:8px 0 0;';
export const ECHARTS_TOOLTIP_VALUE_STYLE =
  'margin:2px 0;font-size:12px;color:#000;';
export const ECHARTS_TOOLTIP_LARGE_ACTION_STYLE =
  'background-color:#fff;border:1px solid #003591;color:#003591;border-radius:10px;padding:8px 12px;margin-top:6px;font-size:14px;font-weight:700;cursor:pointer;';

export const ECHARTS_TOOLTIP_DIVIDER_HTML =
  '<hr style="border:0;border-top:1px solid #9a9a9a;margin:10px 0 8px;">';

export function createEchartsTooltipPointerBridgeCss(maxWidth: number): string {
  return `pointer-events:auto;max-width:${maxWidth}px;background:transparent!important;border:none!important;box-shadow:none!important;border-radius:0!important;`;
}

export interface EchartsTooltipAction {
  label: string;
  payload: EchartsTooltipActionPayload;
}

export interface EchartsTooltipHtmlOptions {
  title: string;
  content: string;
  titlePrefix?: string;
  titleStyle?: string;
  action?: EchartsTooltipAction;
  actionStyle?: string;
}

export interface EchartsTooltipCardHtmlOptions {
  padding?: string;
  shadowOpacity?: number;
  width?: number;
}

export function escapeEchartsTooltipHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function createEchartsTooltipValueRowHtml({
  label,
  value,
  color,
}: {
  label: string;
  value: number | string;
  color?: string;
}): string {
  const marker = color
    ? `<span style="display:inline-block;flex-shrink:0;width:10px;height:10px;border-radius:50%;background:${escapeEchartsTooltipHtml(color)};"></span>`
    : '';

  return `<p style="display:flex;align-items:center;gap:6px;margin:2px 0;font-size:14px;font-weight:400;color:#000;">${marker}<span style="min-width:0;">${escapeEchartsTooltipHtml(label)}: <b>${escapeEchartsTooltipHtml(value)}</b></span></p>`;
}

function encodeEchartsTooltipAttribute(value: unknown): string {
  return escapeEchartsTooltipHtml(JSON.stringify(value))
    .replace(/'/g, '&#39;')
    .replace(/"/g, '&quot;');
}

function escapeEchartsTooltipAttributeValue(value: unknown): string {
  return escapeEchartsTooltipHtml(value).replace(/"/g, '&quot;');
}

export function createEchartsTooltipHtml({
  title,
  content,
  titlePrefix = '',
  titleStyle = ECHARTS_TOOLTIP_DEFAULT_TITLE_STYLE,
  action,
  actionStyle = 'background-color:#fff;border:1px solid #003591;color:#003591;border-radius:8px;padding:6px 10px;margin-top:6px;font-size:12px;font-weight:700;cursor:pointer;',
}: EchartsTooltipHtmlOptions): string {
  const actionHtml = action
    ? `<button type="button" style="${actionStyle}${tooltipWrapStyle}" data-echarts-tooltip-action data-echarts-tooltip-data="${encodeEchartsTooltipAttribute(action.payload)}">${escapeEchartsTooltipHtml(action.label)} &#8594;</button>`
    : '';
  const wrappedTitleStyle = `${titleStyle}${tooltipWrapStyle}`;

  // The native title attribute shows the full text on hover whenever the
  // visible title is truncated (e.g. with a text-overflow:ellipsis style).
  return `<div style="max-width:350px;${tooltipWrapStyle}box-sizing:border-box;"><p style="${wrappedTitleStyle}" title="${escapeEchartsTooltipAttributeValue(title)}">${titlePrefix}${escapeEchartsTooltipHtml(title)}</p>${content}${actionHtml}</div>`;
}

export function createEchartsTooltipCardHtml(
  content: string,
  {
    padding = '8px 10px',
    shadowOpacity = 0.1,
    width = 200,
  }: EchartsTooltipCardHtmlOptions = {},
): string {
  return `<div style="background-color:#fff;box-shadow:0 2px 4px rgba(0,0,0,${shadowOpacity});border-radius:8px;padding:${padding};width:${width}px;box-sizing:border-box;">${content}</div>`;
}
